import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { useLang } from "@/lib/lang-context";
import { getConsent, setConsent } from "@/lib/consent";

/**
 * GDPR cookie banner. Renders only on first visit (no `analytics_consent`
 * key in localStorage). Two actions:
 *   - "Solo essenziali" (default, pre-selected per Italian DPA guidance):
 *     dismiss without granting analytics consent. Essential cookies (session,
 *     auth, load-balancing) continue to work — they don't require consent.
 *   - "Accetta tutti": dismiss + grant analytics consent (GA4 + PostHog).
 * A "manage preferences" link points to /privacy where the user can revisit
 * the choice later.
 *
 * Visual: bottom-of-screen card, mobile-first, sticks above the Emergent
 * badge so neither overlap. Z-index 60 sits ABOVE the paywall modal (50).
 */
const CookieBanner = () => {
  const { t } = useLang();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const c = getConsent();
    if (c !== "granted" && c !== "denied") setVisible(true);
  }, []);

  if (!visible) return null;

  const accept = () => {
    setConsent("granted");
    setVisible(false);
  };
  const reject = () => {
    setConsent("denied");
    setVisible(false);
  };

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-live="polite"
      aria-label={t("cookie.title")}
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-[60]"
    >
      <div className="bg-white border-2 border-[#282C2A] shadow-paper p-5 md:p-6">
        <div className="label-eyebrow mb-2">{t("cookie.eyebrow")}</div>
        <h3 className="font-serif-display text-xl text-[#282C2A] leading-tight">
          {t("cookie.title")}
        </h3>
        <p className="mt-2 text-xs leading-relaxed text-[#6C706D]">
          {t("cookie.body")}{" "}
          <Link
            to="/privacy"
            data-testid="cookie-banner-privacy-link"
            className="text-[#4A5D23] underline hover:text-[#3B4A1C]"
          >
            {t("cookie.learnMore")}
          </Link>
          .
        </p>
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          {/* Default pre-selected option per Italian Garante guidance:
              "Solo essenziali" must be at least as visible as "Accept all". */}
          <button
            type="button"
            onClick={reject}
            data-testid="cookie-reject"
            aria-pressed="true"
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest border-2 border-[#282C2A] bg-[#F0EFEB] text-[#282C2A] hover:bg-[#282C2A] hover:text-white transition-colors relative"
          >
            <Check size={12} strokeWidth={2.5} className="text-[#4A5D23]" />
            {t("cookie.essentialOnly")}
            <span className="absolute -top-2 right-2 bg-[#4A5D23] text-white text-[8px] font-mono uppercase tracking-widest px-1.5 py-0.5">
              {t("cookie.default")}
            </span>
          </button>
          <button
            type="button"
            onClick={accept}
            data-testid="cookie-accept"
            className="flex-1 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest border-2 border-[#4A5D23] bg-[#4A5D23] text-white hover:bg-[#3B4A1C] transition-colors"
          >
            {t("cookie.accept")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
